import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout, Page, Card, Text, BlockStack, Box, Badge, InlineStack } from "@shopify/polaris";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "configureStore";
import { fetchValidationAttemptsByCustomer } from "services/customers.service";
import PaginationControls from "components/pagination/PaginationControls";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import type { ValidationAttemptRow } from "components/customer-form-validation-attempts/validation-attempt-detail-modal";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

type CustomerInfo = {
  id: string;
  name: string;
  legalName: string;
  companyCode: string;
  vatCode: string;
  email: string;
};

export default function ValidationAttemptsByCustomerContainer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { companyId, customerId } = useParams<{
    companyId: string;
    customerId: string;
  }>();
  const { role } = useSelector((state: RootState) => state.authReducer);
  const isMaster =
    !!role?.isMaster || !!role?.permissionsgroups?.includes("Master");

  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [rows, setRows] = useState<ValidationAttemptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [total, setTotal] = useState(0);
  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 20,
  });

  const { page, pageSize } = paginationState;
  const limit = pageSize;
  const skip = (page - 1) * pageSize;

  const openAttemptModal = useCallback(
    (attempt: ValidationAttemptRow) => {
      dispatch(
        setModalxOptions({
          title: t("validation_attempt_detail_title"),
        })
      );
      dispatch(setModalxStyle("large"));
      dispatch(setModalxComponent("ValidationAttemptDetailModal"));
      dispatch(setModalxComponentProps({ attempt }));
    },
    [dispatch, t]
  );

  const runFetch = useCallback(() => {
    if (!companyId || !customerId || !isMaster) return;
    setLoading(true);
    fetchValidationAttemptsByCustomer(companyId, customerId, { limit, skip })
      .then((data) => {
        setCustomer((data.customer || null) as CustomerInfo | null);
        setRows((data.rows || []) as ValidationAttemptRow[]);
        setTotal(typeof data.count === "number" ? data.count : 0);
        setError(false);
      })
      .catch(() => {
        setError(true);
        setCustomer(null);
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [companyId, customerId, isMaster, limit, skip]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  const columns: Column[] = useMemo(
    () => [
      {
        key: "createdAt",
        name: t("validation_attempt_at"),
        type: "clickabledate",
        isShowTime: true,
        mobileView: "topLeft",
        onClick: (item: ValidationAttemptRow) => openAttemptModal(item),
      },
      {
        key: "formKey",
        name: "formKey",
        type: "string",
        mobileView: "topRight",
        onClick: (item: ValidationAttemptRow) => openAttemptModal(item),
      },
      {
        key: "errorKeys",
        name: t("validation_attempt_errors"),
        type: "custom",
        mobileView: "bottomLeft",
        render: (item: ValidationAttemptRow) => {
          const errorKeys = Array.isArray(item.errorKeys) ? item.errorKeys : [];
          return (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => openAttemptModal(item)}
            >
              {errorKeys.length === 0 ? (
                <span>—</span>
              ) : (
                <InlineStack gap="100" wrap>
                  {errorKeys.slice(0, 6).map((key) => (
                    <Badge key={key} tone="critical">
                      {key}
                    </Badge>
                  ))}
                  {errorKeys.length > 6 && (
                    <Badge>{`+${errorKeys.length - 6}`}</Badge>
                  )}
                </InlineStack>
              )}
            </div>
          );
        },
      },
    ],
    [t, openAttemptModal]
  );

  if (!isMaster) {
    return <Navigate to={`/app/${companyId}/settings`} replace />;
  }

  if (!companyId || !customerId) {
    return null;
  }

  const titleName = customer?.name || customer?.legalName || customerId;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
  const hasNextPage = total > 0 && page * pageSize < total;
  const hasPreviousPage = page > 1;

  return (
    <Page
      fullWidth
      title={t("validation_attempt_forms_title", { name: titleName })}
      backAction={{
        content: t("validation_attempt_customers_settings"),
        onAction: () =>
          navigate(
            `/app/${companyId}/settings/customer-form-validation-attempts`
          ),
      }}
    >
      <Layout>
        <Layout.Section>
          <div style={{ marginTop: 5 }}>
            <Card>
              <BlockStack gap="300">
                {customer && (
                  <Text as="p" tone="subdued">
                    {[customer.companyCode, customer.vatCode, customer.email]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </Text>
                )}
                {loading ? (
                  <DataTableSkeleton />
                ) : error ? (
                  <Box padding="400">
                    <Text as="p" tone="critical">
                      {t("error_fetching_data", {
                        defaultValue: "Failed to load data",
                      })}
                    </Text>
                  </Box>
                ) : (
                  <>
                    <DataTable
                      collection={rows}
                      columns={columns}
                      isTotalsDisabled
                    />
                    {total > 0 && (
                      <div
                        style={{
                          borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                          padding: 16,
                        }}
                      >
                        <PaginationControls
                          page={page}
                          pageSize={pageSize}
                          total={total}
                          totalPages={totalPages}
                          hasNextPage={hasNextPage}
                          hasPreviousPage={hasPreviousPage}
                          onPageChange={(nextPage) => {
                            if (nextPage > 0 && nextPage !== page) {
                              setPaginationState((prev) => ({
                                ...prev,
                                page: nextPage,
                              }));
                            }
                          }}
                          onPageSizeChange={(nextPageSize) => {
                            if (
                              nextPageSize > 0 &&
                              nextPageSize !== pageSize
                            ) {
                              setPaginationState({
                                page: 1,
                                pageSize: nextPageSize,
                              });
                            }
                          }}
                          pageSizeOptions={PAGE_SIZE_OPTIONS}
                          isLoading={false}
                        />
                      </div>
                    )}
                  </>
                )}
              </BlockStack>
            </Card>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
